import {Tooltip} from '@mui/material';
import React from 'react';

import {ChipKind, daysUntil, expiryLabel, hoursUntil, initials} from '../lib/aggregate';
import {chipKindClass} from '../lib/chip-style';
import {GrantView} from '../lib/grant-view';

export interface UserAccessChip {
    email: string;
    name: string;
    kind: ChipKind;
    permanent: boolean;
    daysRemaining: number;
    hoursRemaining: number;
    fromBastion: boolean;
    grants: GrantView[];
}

interface Props {
    chip: UserAccessChip;
    onClick?: (email: string) => void;
    /** Drop the expiry badge — used when the expiry is shown in a separate column. */
    hideBadge?: boolean;
}

function badgeLabel(chip: UserAccessChip): string {
    if (chip.permanent) {
        return '∞';
    }
    if (chip.daysRemaining < 0) {
        return 'exp';
    }
    if (chip.daysRemaining === 0 && chip.hoursRemaining >= 0 && chip.hoursRemaining < 24) {
        return `${chip.hoursRemaining}h`;
    }
    return expiryLabel(chip.daysRemaining);
}

function stateClass(chip: UserAccessChip): string {
    if (chip.permanent) {
        return 'permanent';
    }
    if (chip.daysRemaining < 0) {
        return 'expired';
    }
    if (chip.daysRemaining === 0 && chip.hoursRemaining >= 0 && chip.hoursRemaining < 24) {
        return 'expiring-imminent';
    }
    if (chip.daysRemaining <= 7) {
        return 'expiring';
    }
    if (chip.daysRemaining <= 30) {
        return 'expiring-soon';
    }
    return '';
}

function tooltip(chip: UserAccessChip): string {
    const head = `${chip.name || chip.email} · ${chip.email}`;
    if (chip.permanent) {
        return `${head} · permanent`;
    }
    if (chip.daysRemaining < 0) {
        return `${head} · expired`;
    }
    if (chip.daysRemaining === 0 && chip.hoursRemaining >= 0 && chip.hoursRemaining < 24) {
        return `${head} · ${chip.hoursRemaining}h remaining`;
    }
    return `${head} · ${chip.daysRemaining}d remaining`;
}

/**
 * Chip showing a single user's access against a tenant — used in the Tenants tab
 * where rows are tenants and each cell holds the list of users who hold that
 * kind of access. The visual language is the same as AccessChip's so an admin
 * can read row counts and expiry states the same way.
 */
export function UserChip({chip, onClick, hideBadge}: Props) {
    const kindCls = chipKindClass(chip.kind);
    const bastionCls = chip.fromBastion ? 's-bastion' : '';
    const stateCls = stateClass(chip);
    const staticCls = onClick ? '' : 'static';
    const className = ['ac', 'ac-user', kindCls, bastionCls, stateCls, staticCls]
        .filter(Boolean)
        .join(' ');
    const interactive = onClick
        ? {
              role: 'button' as const,
              tabIndex: 0,
              onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onClick(chip.email);
              },
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onClick(chip.email);
                  }
              },
          }
        : {};
    return (
        <Tooltip title={tooltip(chip)} placement="top" arrow>
            <span className={className} {...interactive}>
                <span className="avatar avatar-chip" aria-hidden>
                    {initials(chip.name, chip.email)}
                </span>
                <span className="name">{chip.name || chip.email}</span>
                {!hideBadge && <span className="badge">{badgeLabel(chip)}</span>}
            </span>
        </Tooltip>
    );
}

export {daysUntil, hoursUntil};
